import { connect } from 'react-redux';
import { ThemeContext } from 'styled-components';
import PropTypes from 'prop-types';
import React, { useCallback, useContext, useState } from 'react';

import appSelectors from 'selectors/app';
import CvvField from 'presentation/components/SecurePaymentForm/CvvField';
import ExpField from 'presentation/components/SecurePaymentForm/ExpField';
import getRadius from 'lib/getRadius';
import LoadingPlaceholder from 'presentation/components/icons/LoadingPlaceholder';
import Lock from 'presentation/components/icons/Lock';
import messageSelectors from 'selectors/messages';
import NameField from 'presentation/components/SecurePaymentForm/NameField';
import NumberField from 'presentation/components/SecurePaymentForm/NumberField';
import overrideStyle from 'lib/overrideStyle';
import { shiftColorShade, determineFontColor } from 'lib/colorLogic';
import uiEvents from 'events/ui';
import { useScript } from 'presentation/hooks/useScript';
import userSelectors from 'selectors/user';
import vgsFormInitialState from './vgsFormInitialState';
import declinePayment from 'actions/messages/declinePayment';
import { getFormattedCurrencyAmount } from 'lib/currencyHelpers';

const COLOR_SHIFT_AMOUNT = 4;

export function SecurePaymentForm(props) {
  const theme = useContext(ThemeContext);
  const [form, setForm] = useState(null);
  const [canSubmitForm, setCanSubmitForm] = useState(false);
  const [isPaymentRequestPending, setIsPaymentRequestPending] = useState(false);
  const [formState, setFormState] = useState(vgsFormInitialState);
  const handleDecline = useCallback(() => {
    props.onDecline(props.paymentId);
  }, [props.onDecline, props.paymentId]);

  const handleDeclineKeyDown = ev => {
    if (ev.key === 'Enter') {
      handleDecline();
    }
  };

  if (!props.paymentId) {
    props.onNavigateBack();
    return null;
  }

  const css = {
    borderRadius: '10px',
    boxSizing: 'border-box',
    fontSize: '13px',
    fontFamily: theme.fontLibrary.primary,
    width: '290px',
    top: '0px',
    position: 'absolute',
    transition: 'all 0.2s ease',
  };

  const handlePay = useCallback(
    event => {
      /* eslint-disable no-unused-vars */
      event.preventDefault();
      if (canSubmitForm && !isPaymentRequestPending) {
        setIsPaymentRequestPending(true);
        form.submit(
          `/api/v1/secure-data/cc/orgs/${props.orgId}/customers/${props.gladlyCustomerId}/secure-payment`,
          {
            data: {
              secureItemId: props.paymentId,
            },
            methods: 'POST',
            serializer: 'deep',
            serialization: 'json',
          },
          (status, response) => {
            props.onSubmit();
          },
          errors => {
            throw errors;
          }
        );
      }
      /* eslint-disable no-unused-vars */
    },
    [canSubmitForm, form, isPaymentRequestPending]
  );

  const handlePayKeyDown = ev => {
    if (ev.key === 'Enter') {
      handlePay(ev);
    }
  };

  const renderPayButton = () => {
    let buttonContent;
    if (isPaymentRequestPending) {
      buttonContent = <LoadingPlaceholder color={determineFontColor(theme.colors.brandPrimary)} size="10px" />;
    } else {
      buttonContent = (
        <>
          <LockWrapper>
            <Lock fill={determineFontColor(theme.colors.brandPrimary)} />
          </LockWrapper>
          <PayButtonText>Pay {getFormattedCurrencyAmount(props.currency, props.monetaryUnit)}</PayButtonText>
        </>
      );
    }

    const isDisabled = isPaymentRequestPending || !canSubmitForm;
    return (
      <TabButtonWrapper
        aria-labelledby="submit-payment"
        className="btn btn-sm btn-primary"
        data-aid="securePayment-submit"
        disabled={isDisabled}
        onClick={handlePay}
        onKeyDown={handlePayKeyDown}
        role="button"
        type="submit"
        tabIndex="0"
      >
        <PayButton tabIndex="-1">{buttonContent}</PayButton>
      </TabButtonWrapper>
    );
  };

  const scriptLoadedCallback = useCallback(() => {
    const vgsForm = window.VGSCollect.create(props.vaultId, state => {
      // will only update state once all fields have been populated, this avoids having to check each field for being populated as all of the calls to do so happen sequentially

      if (Object.keys(state).length >= 4) {
        setFormState(state);
        setCanSubmitForm(Object.values(state).every(x => x.isValid));
      }
    });

    setForm(vgsForm);
  }, []);

  useScript(`https://js.verygoodvault.com/vgs-collect/1/${props.secureOrgId}.js`, scriptLoadedCallback);

  if (!form) {
    return null;
  }
  return (
    <Wrapper>
      <CCForm id="gladly-ccform">
        <form id="cc-form">
          <NumberField cssBase={css} form={form} formState={formState.cardNumber} />
          <NameField cssBase={css} form={form} formState={formState.cardName} />
          <FlexWrapper>
            <ExpField cssBase={css} form={form} formState={formState.cardExpirationDate} />
            <CvvField cssBase={css} form={form} formState={formState.cardCvv} />
          </FlexWrapper>
          {renderPayButton()}
          <TabButtonWrapper
            aria-labelledby="decline-payment"
            data-aid="securePayment-decline"
            onClick={handleDecline}
            onKeyDown={handleDeclineKeyDown}
            role="button"
            tabIndex="0"
          >
            <DeclineButton tabIndex="-1">Decline</DeclineButton>
          </TabButtonWrapper>
        </form>
      </CCForm>
    </Wrapper>
  );
}

SecurePaymentForm.propTypes = {
  currency: PropTypes.string.isRequired,
  gladlyCustomerId: PropTypes.string.isRequired,
  monetaryUnit: PropTypes.number.isRequired,
  onDecline: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  orgId: PropTypes.string.isRequired,
  paymentId: PropTypes.string.isRequired,
  secureOrgId: PropTypes.string.isRequired,
  vaultId: PropTypes.string.isRequired,
};

const FlexWrapper = overrideStyle('div')`
  display: flex;
  flex-direction: row;
  margin: 24px 32px 24px 32px;
`;

const Button = overrideStyle('div')`
  box-shadow: none;
  border-radius: ${p => getRadius(p.theme.borderRadius, 4, 8)};
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 30px;
  justify-content: center;
  outline: none;
  transition: 0.2s ease;
`;

const DeclineButton = overrideStyle(Button)`
  cursor: pointer;
  font-family: ${p => p.theme.fontLibrary.primary};
  font-size: 13px;
  font-weight: 500;
  margin-top: 8px;
  text-align: center;

  &:active{
    color: #484848;
  }
  &:hover{
    color: #484848;
  }
`;

const TabButtonWrapper = overrideStyle('div')`
  margin-left: 32px;
  margin-right: 32px;

  &:focus {
    box-shadow: inset 0 0 0px 2px #81b5ea;
    outline: solid 1px #81b5ea;
  }
`;

const PayButton = overrideStyle(Button)`
  align-items: center;
  background-color: ${p => p.theme.colors.brandPrimary};
  border: 1px solid ${p => p.theme.colors.brandPrimary};
  color: ${p => determineFontColor(p.theme.colors.brandPrimary)};
  flex-direction: row;
  height: 40px;
  justify-content: center;

  &:active{
    background-color: ${p => shiftColorShade(p.theme.colors.brandPrimary, -COLOR_SHIFT_AMOUNT)};
    border: 1px solid ${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
  }
  &:hover{
    background-color: ${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
    border: 1px solid ${p => shiftColorShade(p.theme.colors.brandPrimary, COLOR_SHIFT_AMOUNT)};
  }
`;

const PayButtonText = overrideStyle('div')`
  cursor: pointer;
  font-family: ${p => p.theme.fontLibrary.primary};
  font-size: 16px;
  font-weight: bold;
  text-align: center;
`;

const LockWrapper = overrideStyle('div')`
  & svg {
    cursor: pointer;
  }

  cursor: pointer;
  margin-right: 8px;
`;

const CCForm = overrideStyle('div')`
  background-color: #f0f0f0; 
  display: flex;
  flex-direction: column;
  top: 64px;
  left: 0px;
  position: absolute;
  height: 250px;
  width: 100%;

  & iframe {
    height: 30px;
    width: 290px;
  }
`;

const Wrapper = overrideStyle('div')`
  align-items: center;  
  background-color: #F0F0F0; 
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
`;

export const mapStateToProps = state => {
  const paymentAssets = messageSelectors.getPaymentAssets(state.messages);

  return {
    currency: paymentAssets.currency,
    monetaryUnit: paymentAssets.monetaryUnit,
    gladlyCustomerId: userSelectors.getGladlyCustomerId(state.user),
    paymentId: paymentAssets.paymentId,
    orgId: appSelectors.getOrgId(state),
    secureOrgId: appSelectors.getSecureOrgId(state),
    vaultId: appSelectors.getVaultId(state),
  };
};

export const mapDispatchToProps = dispatch => {
  return {
    onNavigateBack: () => {
      dispatch(uiEvents.navigateBack());
    },
    onSubmit: () => {
      dispatch(uiEvents.navigateBack());
    },
    onDecline: paymentId => {
      dispatch(declinePayment(paymentId));
      dispatch(uiEvents.navigateBack());
    },
  };
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(SecurePaymentForm);
